
import { useState, useEffect } from 'react';

const SALE_DEALS = [
  { label: 'Toys', discount: '30% OFF', color: 'from-rose-500 to-pink-500' },
  { label: 'Food & Treats', discount: '20% OFF', color: 'from-amber-500 to-orange-500' },
  { label: 'Grooming', discount: '25% OFF', color: 'from-teal-500 to-emerald-500' },
];

// Sale ends: 48 hours from a fixed reference point (resets every 48h)
function getSaleEndTime(): Date {
  const now = new Date();
  const epoch = new Date('2024-01-01T00:00:00Z').getTime();
  const elapsed = now.getTime() - epoch;
  const cycle = 48 * 60 * 60 * 1000;
  const nextEnd = epoch + Math.ceil(elapsed / cycle) * cycle;
  return new Date(nextEnd);
}

export default function SaleBanner() {
  const [timeLeft, setTimeLeft] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [dismissed, setDismissed] = useState(false);
  const [activeDeal, setActiveDeal] = useState(0);
  const [pulse, setPulse] = useState(false);

  useEffect(() => {
    const tick = () => {
      const end = getSaleEndTime();
      const diff = end.getTime() - Date.now();
      if (diff <= 0) {
        setTimeLeft({ hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setTimeLeft({ hours, minutes, seconds });
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  // Rotate deal highlight every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setPulse(true);
      setTimeout(() => {
        setActiveDeal((prev) => (prev + 1) % SALE_DEALS.length);
        setPulse(false);
      }, 300);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  if (dismissed) return null;

  const pad = (n: number) => String(n).padStart(2, '0');

  return (
    <div className="relative overflow-hidden bg-gradient-to-r from-rose-600 via-orange-500 to-amber-500 shadow-xl">
      {/* Animated shimmer overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-[shimmer_2.5s_infinite] pointer-events-none" />

      {/* Dismiss button */}
      <button
        onClick={() => setDismissed(true)}
        className="absolute top-2 right-3 z-10 w-7 h-7 flex items-center justify-center text-white/70 hover:text-white hover:bg-white/20 rounded-full transition-colors cursor-pointer"
        aria-label="Dismiss sale banner"
      >
        <i className="ri-close-line text-lg"></i>
      </button>

      <div className="max-w-7xl mx-auto px-4 py-3 pr-10">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6 text-white text-center">

          {/* Flash Sale Label */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="animate-bounce text-xl">🔥</span>
            <span className="font-black text-sm sm:text-base uppercase tracking-widest whitespace-nowrap">
              Flash Sale
            </span>
          </div>

          {/* Rotating Deal */}
          <div
            className={`flex items-center gap-2 transition-opacity duration-300 ${pulse ? 'opacity-0' : 'opacity-100'}`}
          >
            <span className={`bg-gradient-to-r ${SALE_DEALS[activeDeal].color} text-white text-xs font-black px-3 py-1 rounded-full shadow-md whitespace-nowrap`}>
              {SALE_DEALS[activeDeal].discount}
            </span>
            <span className="text-white/90 text-sm font-semibold whitespace-nowrap">
              on {SALE_DEALS[activeDeal].label}
            </span>
          </div>

          {/* Divider */}
          <div className="hidden sm:block w-px h-6 bg-white/30 flex-shrink-0" />

          {/* Countdown */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <i className="ri-time-line text-white/80 text-base"></i>
            <span className="text-white/80 text-xs font-semibold uppercase tracking-wide whitespace-nowrap">
              Ends in
            </span>
            <div className="flex items-center gap-1">
              {/* Hours */}
              <div className="flex flex-col items-center">
                <div className="bg-white/20 backdrop-blur-sm rounded-md px-2 py-1 min-w-[36px] text-center">
                  <span className="font-black text-base sm:text-lg tabular-nums leading-none">
                    {pad(timeLeft.hours)}
                  </span>
                </div>
                <span className="text-white/60 text-[9px] uppercase tracking-wide mt-0.5">hrs</span>
              </div>
              <span className="font-black text-lg mb-3">:</span>
              {/* Minutes */}
              <div className="flex flex-col items-center">
                <div className="bg-white/20 backdrop-blur-sm rounded-md px-2 py-1 min-w-[36px] text-center">
                  <span className="font-black text-base sm:text-lg tabular-nums leading-none">
                    {pad(timeLeft.minutes)}
                  </span>
                </div>
                <span className="text-white/60 text-[9px] uppercase tracking-wide mt-0.5">min</span>
              </div>
              <span className="font-black text-lg mb-3">:</span>
              {/* Seconds */}
              <div className="flex flex-col items-center">
                <div className="bg-white/20 backdrop-blur-sm rounded-md px-2 py-1 min-w-[36px] text-center">
                  <span className="font-black text-base sm:text-lg tabular-nums leading-none">
                    {pad(timeLeft.seconds)}
                  </span>
                </div>
                <span className="text-white/60 text-[9px] uppercase tracking-wide mt-0.5">sec</span>
              </div>
            </div>
          </div>

          {/* CTA */}
          <button
            onClick={() => {
              const el = document.getElementById('product-grid');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            className="flex-shrink-0 bg-white text-orange-600 font-black text-xs sm:text-sm px-4 py-2 rounded-full shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all whitespace-nowrap cursor-pointer"
          >
            Shop Now →
          </button>
        </div>
      </div>
    </div>
  );
}
